import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { uploader } from '@gem/modules/file-upload';
import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';

interface IUploadBackgroundPresenterParams {
  file: File;
  onUploadSuccess(url: string): void;
  onCancelUpload(): void;
}

interface IUploadBackgroundPresenter {
  uploadProgress: number | null;
  url: string | null;
  uploadUrl: string | null;
  upload(): Promise<void>;
  cancelUpload(): Promise<void>;
  previewUrl: string;
}

export const useUploadBackgroundPresenter = ({
  file,
  onUploadSuccess,
  onCancelUpload,
}: IUploadBackgroundPresenterParams): IUploadBackgroundPresenter => {
  const { t } = useTranslation();
  const presenter = useLocalObservable<IUploadBackgroundPresenter>(() => ({
    uploadProgress: null,
    url: null,
    uploadUrl: null,

    get previewUrl(): string {
      return URL.createObjectURL(file);
    },

    async upload(): Promise<void> {
      if (!file) {
        return;
      }

      presenter.uploadProgress = 0;

      if (!presenter.url) {
        try {
          const result = await uploader.uploadFileWithUrl(
            file,
            file.name,
            file.type,
            (uploadUrl) => {
              presenter.uploadUrl = uploadUrl;
            },
            (event) => {
              presenter.uploadProgress = (event.loaded / event.total) * 100;
            }
          );

          onUploadSuccess(result.publicUrl);
        } catch (e) {
          presenter.uploadProgress = 0;
          onCancelUpload();
          Tasks.app.showToast(t('background_error'), { style: '4' });
          return;
        }
      }

      presenter.uploadProgress = null;
    },

    async cancelUpload(): Promise<void> {
      if (presenter.uploadUrl) {
        uploader.cancelUploadFile(presenter.uploadUrl);
        onCancelUpload();
      }
    },
  }));

  useEffect(() => {
    presenter.upload();
  }, []);

  return presenter;
};
